import React, { FC, ReactElement, useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import InfoIcon from "@/assets/svgs/info.svg";
import FactoryIcon from "@mui/icons-material/Factory";
import FlagIcon from "@mui/icons-material/Flag";
import HighRiskIcon from "@/assets/svgs/HighRisk.svg";
import MediumRiskIcon from "@/assets/svgs/MediumRisk.svg";
import LowRiskIcon from "@/assets/svgs/LowRisk.svg";
import HelpIcon from "@mui/icons-material/Help";
import { DynamicTable, RiskAnalysisTooltip, HTSCodeWithTooltip } from "@/components";
import { ProductWithTariffs, LineType } from "@/services/product-catalogue";
import { getCountryFlag } from "@/constants/country-flags";
import TariffBreakdownPopover from "../tariff-breakdown-popover";

interface ProductTableProps {
  data: ProductWithTariffs[];
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  totalCount: number;
  onEditProduct: (product: ProductWithTariffs) => void;
  onDeleteProduct: (productId: string) => void;
  isProductPending?: (productId: string) => boolean;
}

// Reusable component for truncated text with hover tooltip
const TruncatedTextCell: React.FC<{ text: string }> = ({ text }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const displayText = text || "-";

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isOverflowing);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [displayText]);

  const tooltipProps = {
    title: displayText,
    arrow: true,
    placement: "top" as const,
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, -8] },
          },
        ],
      },
      tooltip: {
        sx: {
          bgcolor: '#1e293b',
          color: 'white',
          fontSize: '0.875rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
      },
    },
  };

  const cellSx = { 
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#171717'
  };

  const content = (
    <Box ref={textRef} sx={cellSx}>
      {displayText}
    </Box>
  );

  if (isTruncated) {
    return (
      <Tooltip {...tooltipProps}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

const DescriptionCell: React.FC<{ text: string }> = ({ text }) => {
  return <TruncatedTextCell text={text} />;
};

const ProductTable: FC<ProductTableProps> = ({
  data,
  currentPage,
  pageSize,
  isLoading,
  onPageChange,
  onPageSizeChange,
  totalCount,
  onEditProduct,
  onDeleteProduct,
  isProductPending,
}): ReactElement => {
  // State for breakdown popover
  const [breakdownAnchor, setBreakdownAnchor] = useState<HTMLElement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithTariffs | null>(null);
  
  const handleOpenBreakdown = (event: React.MouseEvent<HTMLElement>, product: ProductWithTariffs) => {
    setBreakdownAnchor(event.currentTarget);
    setSelectedProduct(product);
  };
  
  const handleCloseBreakdown = () => {
    setBreakdownAnchor(null);
    setSelectedProduct(null);
  };

  // Build dynamic columns including tariff columns
  const columns = [
    {
      key: "sku" as keyof ProductWithTariffs,
      name: "SKU",
      align: "left" as const,
      width: "13%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <TruncatedTextCell text={row.sku} />
      ),
    },
    {
      key: "name" as keyof ProductWithTariffs,
      name: "Product Name",
      align: "left" as const,
      width: "16%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <TruncatedTextCell text={row.name} />
      ),
    },
    {
      key: "description" as keyof ProductWithTariffs,
      name: "Description",
      align: "left" as const,
      width: "20%",
      component: (row: ProductWithTariffs) => (
        <DescriptionCell text={row.description || "-"} />
      ),
    },
    {
      key: "htsCode" as keyof ProductWithTariffs,
      name: "HTS Code",
      align: "left" as const,
      width: "14%",
      sorting: true,
      component: (row: ProductWithTariffs) => {
        const hasGeneralDuty = row.breakdownLines?.some(line => 
          line.calculationBreakdown?.some(calc => 
            calc.applied && (
              calc.columnName.toLowerCase().includes('general duty') ||
              calc.columnName.toLowerCase().includes('hts') ||
              calc.columnId === 'general-duty'
            )
          )
        );
        return (
          <HTSCodeWithTooltip htsCode={row.htsCode}>
            <Typography
              sx={{
                color: !hasGeneralDuty ? '#ef4444' : '#171717',
                fontWeight: !hasGeneralDuty ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '14px',
                lineHeight: '20px',
                cursor: 'pointer',
                '&:hover': {
                  color: '#0b494b',
                  textDecoration: 'underline',
                },
              }}
            >
              {row.htsCode}
            </Typography>
          </HTSCodeWithTooltip>
        );
      },
    },
    {
      key: "countryOfOrigin" as keyof ProductWithTariffs,
      name: "Country",
      align: "left" as const,
      width: "10%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>{getCountryFlag(row.countryOfOrigin)}</span>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: '#171717' }}>
            {row.countryOfOrigin}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "riskAnalysis" as keyof ProductWithTariffs,
      name: "AI Risk Score",
      align: "left" as const,
      width: "10%",
      component: (row: ProductWithTariffs) => {
        // Check if this product is pending analysis
        const isPending = isProductPending && isProductPending(row._id);
        
        if (isPending) {
          return (
            <RiskAnalysisTooltip riskAnalysis={{ riskLevel: 'PENDING', riskReason: 'Analysis pending...', analyzedAt: new Date().toISOString(), analysisVersion: '1.0' }}>
              <Box sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #ebebeb',
                borderRadius: '6px',
                padding: '4px 8px 4px 4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <CircularProgress size={12} sx={{ color: '#6B7280' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5c5c5c' }}>
                  Pending
                </Typography>
              </Box>
            </RiskAnalysisTooltip>
          );
        }
        
        if (!row.riskAnalysis) {
          return (
            <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: '#171717' }}>
              N/A
            </Typography>
          );
        }
        
        const level = row.riskAnalysis.riskLevel.toLowerCase();
        // Map risk level to display text (capitalize)
        const displayText = level.charAt(0).toUpperCase() + level.slice(1);
        
        let iconSrc;
        if (level === 'high') {
            iconSrc = HighRiskIcon;
        } else if (level === 'medium') {
            iconSrc = MediumRiskIcon;
        } else if (level === 'low') {
            iconSrc = LowRiskIcon;
        }

        return (
          <RiskAnalysisTooltip riskAnalysis={row.riskAnalysis}>
            <Box sx={{ 
              backgroundColor: '#ffffff',
              border: '1px solid #ebebeb',
              borderRadius: '6px',
              padding: '4px 8px 4px 4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {iconSrc ? (
                <img src={iconSrc} alt={`${displayText} risk`} style={{ width: 16, height: 16 }} />
              ) : (
                <HelpIcon sx={{ fontSize: 16, color: '#6B7280' }} />
              )}
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5c5c5c' }}>
                {displayText}
              </Typography>
            </Box>
          </RiskAnalysisTooltip>
        );
      },
    },
    // Total column with breakdown
    {
      key: "total-tariffs" as keyof ProductWithTariffs,
      name: "Total Duty",
      align: "left" as const,
      width: "15%",
      component: (row: ProductWithTariffs) => {
        const totalRate = (row.totalDutyRate ?? row.additionalTariffs.reduce((sum, tariff) => sum + tariff.rate, 0));
        // Check if there's a metal/non-metal split in the breakdown
        const hasMetalSplit = row.breakdownLines && row.breakdownLines.length > 0 && 
          row.breakdownLines.some(line => line.lineType === LineType.METAL || line.lineType === LineType.NONMETAL);
        const activeDutyCount = row.additionalTariffs.filter(t => t.rate > 0).length;
        
        // Check if HTS General Duty is missing
        const hasGeneralDuty = row.breakdownLines?.some(line => 
          line.calculationBreakdown?.some(calc => 
            calc.applied && (
              calc.columnName.toLowerCase().includes('general duty') ||
              calc.columnName.toLowerCase().includes('hts') ||
              calc.columnId === 'general-duty'
            )
          )
        );
        
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: '#171717' }}>
              {(totalRate * 100).toFixed(2)}%
            </Typography>
            
            {/* Info Icon Trigger */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {row.additionalTariffs.length > 0 && (
                  <Tooltip 
                    title={`View ${activeDutyCount} duty type${activeDutyCount !== 1 ? 's' : ''} breakdown`}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenBreakdown(e, row)}
                      sx={{ 
                        p: 0, 
                        color: '#8E9494',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {!hasGeneralDuty && (
                  <Tooltip title="HTS code not found in database" placement="top">
                    <FlagIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                  </Tooltip>
                )}
                {hasMetalSplit && (
                  <Tooltip title="Duty calculation split by metal/non-metal content" placement="top">
                    <FactoryIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                  </Tooltip>
                )}
            </Box>
          </Stack>
        );
      },
    },
    {
      key: "actions" as const,
      name: "",
      align: "right" as const,
      width: "6%",
      component: (row: ProductWithTariffs) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Edit Product">
            <IconButton size="small" onClick={() => onEditProduct(row)} sx={{ color: '#171717' }}>
              <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Product">
            <IconButton size="small" onClick={() => onDeleteProduct(row._id)} sx={{ color: '#171717' }}>
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={3} sx={{ height: '100%' }}>
      {/* Table */}
      <DynamicTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        limit={pageSize}
        page={currentPage}
        total={totalCount}
        handlePageChange={onPageChange}
        handleLimitChange={onPageSizeChange}
        stickyHeader={true}
        hideScrollBars={false}
        isFullHeight={true}
        square={false}
        borderLess={true}
        sx={{
          '& .MuiTableContainer-root': { 
            '&::-webkit-scrollbar': { height: 6, width: 6 },
            backgroundColor: '#FFFFFF',
            border: 'none',
          },
          '& .MuiTable-root': {
            tableLayout: 'fixed',
            width: '100%',
            minWidth: { xs: 800, md: '100%' },
            border: 'none',
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: '#F7F7F7',
              color: '#030712',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              padding: '8px 12px',
              border: 'none',
              borderTop: 'none',
              borderRight: 'none',
              borderBottom: 'none',
              borderLeft: 'none',
              '&:first-of-type': {
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
              },
              '&:last-of-type': {
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
              },
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              backgroundColor: '#FFFFFF',
              color: '#171717',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              padding: '12px 20px 12px 12px',
              borderTop: 'none',
              borderRight: 'none',
              borderBottom: '1px solid #EBEBEB',
              borderLeft: 'none',
            },
            '& .MuiTableRow-root': {
              height: '48px',
              border: 'none',
              '&:last-child .MuiTableCell-root': {
                borderBottom: 'none',
              },
            },
            '& .MuiTableHead-root': {
              border: 'none',
            },
            '& .MuiTableBody-root': {
              border: 'none',
            },
          },
        }}
      />
      
      {/* Tariff Breakdown Popover */}
      {selectedProduct && (
        <TariffBreakdownPopover
          anchorEl={breakdownAnchor}
          open={Boolean(breakdownAnchor)}
          onClose={handleCloseBreakdown}
          breakdownLines={selectedProduct.breakdownLines}
          additionalTariffs={selectedProduct.additionalTariffs}
          totalRate={selectedProduct.totalDutyRate ?? selectedProduct.additionalTariffs.reduce((sum, t) => sum + t.rate, 0)}
          metalPercentage={selectedProduct.metalPercentage}
          ftaApplicable={selectedProduct.ftaApplicable}
          ftaProgramName={selectedProduct.ftaProgramName}
          secondaryDutyRateString={(selectedProduct as any)?.secondaryDutyRateString}
        />
      )}
    </Stack>
  );
};

export default React.memo(ProductTable, (prevProps, nextProps) => {
  // Custom comparison function for optimization
  // Only re-render if data actually changed
  return (
    prevProps.data === nextProps.data &&
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.totalCount === nextProps.totalCount
  );
});
